import {useReducer, useEffect} from "react";

import {usePlanner} from "../index";

/*
 * This component is responsible for loading the data for the
 * given range of dates.
 * It calls the eventsLoader and gridDataLoader functions provided by
 * the planner context and pass the result to the given Month component.
 *
 * The Month component is a component that expects:
 * - month: the month to render integer
 * - year: the year to render integer
 * - style: the style property for virtualized list
 * - contentData: an object with the following structure: { resourceId: [contentData1, contentData2, ...], ... }
 *   For each resource is expected an array of any size with the content/event to plot. Each content will be
 *   passed to the renderEvent function of the Planner.
 * - gridData: an object with the following structure: { resourceId: [dataDay1, dataDay2, ..., dataDayN], ... }
 *   For each resource is expected an array of length equal to the number of days in the month. Each value will
 *   be passed to the renderDayCell function of the resource.
 */
const BaseMonthDataLoader = ({Month, dateStart, dateEnd, ...props}) => {
    const {eventsLoader: contentDataLoader, gridDataLoader} = usePlanner();

    const [state, updateState] = useReducer(
        (oldState, newState) => ({...oldState, ...newState}),
        {
            contentData: {},
            gridData: {},
            isLoading: true
        }
    )

    useEffect(
        () => {
            const innerLoader = async () => {
                if (!state.isLoading) {
                    updateState({isLoading: true})
                }

                try{
                    const [contentData, gridData] = await Promise.all([
                        contentDataLoader(dateStart, dateEnd),
                        gridDataLoader(dateStart, dateEnd)
                    ]);
                    updateState({contentData: contentData, gridData: gridData, isLoading: false});
                } catch {
                    updateState({isLoading: false})
                }
            }
            innerLoader();
        }
        , [contentDataLoader, gridDataLoader]  // eslint-disable-line react-hooks/exhaustive-deps
    );
    return <Month {...props} contentData={state.contentData} gridData={state.gridData} isLoading={state.isLoading}/>
}

export default BaseMonthDataLoader;